import { useState, useEffect, useRef } from "react";
import { useFirmwareProgress } from "../../hooks/useFirmwareProgress";
import Icon from "../../components/ui/Icon";
import SlideOverlay from "../../components/overlays/SlideOverlay";
import { STORAGE_KEY, useAppState } from "../../state/appState";
import { FIRMWARE_CATALOG, getAvailableUpdates } from "../../constants/firmware";
import { buildNotifications } from "../../utils/notifications";
import PageHeader from "../../components/shared/PageHeader";
import FirmwarePreCheck from "../firmware/FirmwarePreCheck";
import FirmwareProgress from "../firmware/FirmwareProgress";
import FirmwareDone from "../firmware/FirmwareDone";
import FirmwareChangelog from "../firmware/FirmwareChangelog";
import DiagnosticsPage from "./diagnostics/DiagnosticsPage";
import DiagnosticsDtcPage from "./diagnostics/DiagnosticsDtcPage";
import FirmwareUpdatesPage from "./firmware/FirmwareUpdatesPage";
import NotificationsPage from "./notifications/NotificationsPage";
import ConnectGatewayPage from "./gateway/ConnectGatewayPage";

export default function ConfigView({ initialPage = "main", onConsumeInitialPage }) {
  const { nodes, devices, actions, updatedFirmware, gatewayConnected } = useAppState();

  const [page, setPage] = useState(initialPage);

  useEffect(() => {
    onConsumeInitialPage?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [firmwareTargetId, setFirmwareTargetId] = useState(null);

  const { percent: firmwareProgressPercent, step: firmwareStepIndex } = useFirmwareProgress(
    page === "firmware-progress"
  );

  const handleReset = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
      window.location.reload();
    }
  };

  const lastSubPageRef = useRef(null);
  if (page !== "main") lastSubPageRef.current = page;
  const visiblePage = page !== "main" ? page : lastSubPageRef.current;

  const firmwareEntry = firmwareTargetId ? FIRMWARE_CATALOG[firmwareTargetId] : null;
  const availableUpdates = getAvailableUpdates(updatedFirmware).filter(
    (entry) => nodes.some((n) => n.id === entry.nodeId)
  );

  const regularNodes = nodes.filter((n) => n.category !== "Gateway" && n.id !== "bt-bridge");
  const notifications = regularNodes.length > 0
    ? buildNotifications({ availableUpdates, nodes: regularNodes, devices })
    : [];

  const availableUpdateCount = availableUpdates.length;
  const notificationCount = notifications.length;
  const btcNode = nodes.find((n) => n.id === "bt-bridge" || n.category === "Gateway");
  const connectionLabel = gatewayConnected
    ? btcNode?.name
      ? `cBerry ${btcNode.name.split(" ").pop()}`
      : "Connected"
    : null;

  const menuRow = (icon, label, { trailing, badge, onPress, danger } = {}) => (
    <button
      key={label}
      type="button"
      onClick={onPress}
      className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition ${
        danger ? "hover:bg-red-900/10" : "hover:bg-white/5"
      }`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
          danger
            ? "border-red-500/30 bg-red-900/20"
            : "border-brand-navy/50 bg-brand-navy"
        }`}
      >
        <Icon
          name={icon}
          className={`text-base ${danger ? "text-red-400" : "text-brand-light/60"}`}
        />
      </span>
      <div className="flex-1 flex flex-col min-w-0">
        <span className={`text-sm font-medium ${danger ? "text-red-300" : "text-brand-light"}`}>
          {label}
        </span>
        {danger && (
          <span className="text-[10px] text-red-400/60">Clears all local data and reloads</span>
        )}
      </div>
      {trailing && (
        <span className="text-[11px] text-brand-light/40 shrink-0 mr-1">{trailing}</span>
      )}
      {badge && (
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-magenta/15 text-brand-magenta border border-brand-magenta/30 shrink-0">
          {badge}
        </span>
      )}
      <Icon
        name="chevron_right"
        className={`text-base shrink-0 ${danger ? "text-red-400/50" : "text-brand-magenta"}`}
      />
    </button>
  );

  const menuSection = (heading, rows) => (
    <div className="space-y-1.5">
      <h3 className="px-1.5 text-[11px] font-black italic tracking-[0.18em] text-brand-light/40 uppercase">
        {heading}
      </h3>
      <div className="rounded-3xl border border-brand-navy bg-brand-purple overflow-hidden divide-y divide-brand-navy/40">
        {rows}
      </div>
    </div>
  );

  return (
    <div className="relative h-full">
      <div className="absolute inset-0 overflow-y-auto">
        <div className="px-5 pt-2 pb-6 space-y-5">
          <div className="flex items-center gap-2 rounded-2xl border border-brand-navy bg-brand-purple px-4 py-2.5">
            <Icon name="search" className="text-lg text-brand-light/40 shrink-0" />
            <input
              type="text"
              placeholder="Search settings…"
              className="flex-1 bg-transparent text-xs text-brand-light placeholder:text-brand-light/40 focus:outline-none"
            />
          </div>

          <button
            type="button"
            className="w-full rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 flex items-center gap-4 hover:bg-white/5 transition text-left"
          >
            <div className="h-14 w-14 rounded-full bg-brand-navy border border-brand-navy/50 flex items-center justify-center shrink-0 overflow-hidden">
              <Icon name="person" className="text-3xl text-brand-light/30" />
            </div>
            <div className="flex-1 flex flex-col gap-0.5 min-w-0">
              <span className="text-base font-black italic text-brand-light">Jordan Walke</span>
              <span className="text-[11px] text-brand-light/50">jordan.walke@cberry.de</span>
              <span className="mt-1.5 inline-flex items-center self-start rounded-full bg-brand-magenta px-2.5 py-0.5 text-[11px] font-semibold text-white">
                Test User
              </span>
            </div>
            <Icon name="chevron_right" className="text-brand-magenta text-xl shrink-0" />
          </button>

          {menuSection("General", [
            menuRow("palette", "Appearance"),
            menuRow("notifications", "Notifications", {
              badge: notificationCount > 0 ? `${notificationCount}` : null,
              onPress: () => setPage("notifications"),
            }),
            menuRow("language", "Language"),
          ])}

          {menuSection("System", [
            menuRow("link", "Connection", {
              trailing: connectionLabel,
              onPress: () => setPage("gateway"),
            }),
            menuRow("download", "Firmware Updates", {
              badge: availableUpdateCount > 0 ? `${availableUpdateCount} Available` : null,
              trailing: nodes.length === 0 ? "No network" : null,
              onPress: () => setPage("firmware"),
            }),
            menuRow("analytics", "Diagnostics", {
              onPress: () => setPage("diagnostics"),
            }),
          ])}

          {menuSection("About", [
            menuRow("info", "App Info", { trailing: "v0.1.0" }),
            menuRow("policy", "Privacy Policy"),
            menuRow("article", "Terms of Service"),
            menuRow("restore", "Reset App Data", { danger: true, onPress: handleReset }),
          ])}
        </div>
      </div>

      <SlideOverlay isOpen={page !== "main"}>
        <div className="h-full overflow-y-auto bg-brand-navy">
          <div className="px-5 pb-6 space-y-4">

            {visiblePage === "notifications" && (
              <>
                <PageHeader title="Notifications" onBack={() => setPage("main")} />
                <NotificationsPage
                  notifications={notifications}
                  gatewayConnected={gatewayConnected}
                  nodesScanned={regularNodes.length > 0}
                  onNavigate={(target) => setPage(target)}
                  onConnectToGateway={() => setPage("gateway")}
                  onScanNetwork={actions.startDiscovery}
                />
              </>
            )}

            {visiblePage === "gateway" && (
              <>
                <PageHeader title="Connection" onBack={() => setPage("main")} />
                <ConnectGatewayPage
                  onStartDiscovery={actions.startDiscovery}
                  onDisconnect={actions.disconnect}
                />
              </>
            )}

            {visiblePage === "diagnostics" && (
              <>
                <PageHeader title="Diagnostics" onBack={() => setPage("main")} />
                <DiagnosticsPage onOpenDtcPage={() => setPage("dtc")} />
              </>
            )}

            {visiblePage === "dtc" && (
              <>
                <PageHeader title="Active Error Codes" onBack={() => setPage("diagnostics")} />
                <DiagnosticsDtcPage />
              </>
            )}

            {visiblePage === "firmware" && (
              <>
                <PageHeader title="Firmware Updates" onBack={() => setPage("main")} />
                <FirmwareUpdatesPage
                  updateEntries={availableUpdates}
                  onStartNodeUpdate={(nodeId) => {
                    setFirmwareTargetId(nodeId);
                    setPage("firmware-node");
                  }}
                  onViewChangelog={(nodeId) => {
                    setFirmwareTargetId(nodeId);
                    setPage("firmware-changelog");
                  }}
                />
              </>
            )}

            {visiblePage === "firmware-changelog" && (
              <>
                <PageHeader title="Changelog" onBack={() => setPage("firmware")} />
                <FirmwareChangelog updateEntry={firmwareEntry} />
              </>
            )}

            {visiblePage === "firmware-node" && (
              <>
                <PageHeader title="Firmware Update" onBack={() => setPage("firmware")} />
                <FirmwarePreCheck
                  updateEntry={firmwareEntry}
                  onStartUpdate={() => setPage("firmware-progress")}
                />
              </>
            )}

            {visiblePage === "firmware-progress" && (
              <>
                <PageHeader title="Firmware Update" onBack={() => {}} backDisabled />
                {firmwareProgressPercent >= 100 ? (
                  <FirmwareDone
                    updateEntry={firmwareEntry}
                    onDone={() => {
                      if (firmwareEntry) {
                        actions.markFirmwareUpdated(firmwareEntry.nodeId, firmwareEntry.availableVersion);
                      }
                      setPage("firmware");
                    }}
                  />
                ) : (
                  <FirmwareProgress
                    updateEntry={firmwareEntry}
                    percent={firmwareProgressPercent}
                    step={firmwareStepIndex}
                    onCancel={() => setPage("firmware")}
                  />
                )}
              </>
            )}

          </div>
        </div>
      </SlideOverlay>
    </div>
  );
}
